import { useEffect, useMemo } from 'react';

import { CollectionsV1ResourceType } from '@spotify/confidence-graphql';

import { useCollectionEntries } from './useCollectionEntries';

const RESOURCE_TYPE_TO_TYPENAME: Record<CollectionsV1ResourceType, string> = {
  [CollectionsV1ResourceType.Flag]: 'FlagsAdminV1Flag',
  [CollectionsV1ResourceType.Metric]: 'MetricsV1Metric',
  [CollectionsV1ResourceType.WorkflowInstance]: 'WorkflowV1WorkflowInstance',
  [CollectionsV1ResourceType.FactTable]: 'MetricsV1FactTable',
  [CollectionsV1ResourceType.AssignmentTable]: 'MetricsV1AssignmentTable',
  [CollectionsV1ResourceType.DimensionTable]: 'MetricsV1DimensionTable',
  [CollectionsV1ResourceType.Initiative]: 'WorkflowV1Initiative',
  [CollectionsV1ResourceType.Project]: 'CollaborationV1Project',
  [CollectionsV1ResourceType.Segment]: 'FlagsAdminV1Segment',
  [CollectionsV1ResourceType.Surface]: 'WorkflowV1Surface',
  [CollectionsV1ResourceType.ExternalDocument]:
    'CollaborationV1ExternalDocument',
  [CollectionsV1ResourceType.Issue]: 'ObservationsV1Issue',
};

export const useCollectionResourceNames = (
  collectionName: string | null,
  options?: { resourceType?: CollectionsV1ResourceType },
): { resourceNames: Set<string>; loading: boolean } => {
  const { collectionEntries, isLoading, hasNextPage, nextPage } =
    useCollectionEntries({
      parent: collectionName ?? '',
      skip: !collectionName,
    });

  // TODO: Fetches all pages eagerly to build the full set of resource names for filtering
  // Revisit when the backend supports filtering by collection
  useEffect(() => {
    if (hasNextPage && !isLoading) {
      nextPage();
    }
  }, [hasNextPage, isLoading, nextPage]);

  const typename = options?.resourceType
    ? RESOURCE_TYPE_TO_TYPENAME[options.resourceType]
    : undefined;

  const resourceNames = useMemo(
    () =>
      new Set(
        (collectionEntries ?? [])
          .map(entry => entry.resource)
          .filter(
            (
              resource,
            ): resource is NonNullable<typeof resource> & { name: string } =>
              !!resource &&
              'name' in resource &&
              (!typename || resource.__typename === typename),
          )
          .map(resource => resource.name),
      ),
    [collectionEntries, typename],
  );

  return {
    resourceNames,
    loading:
      isLoading || hasNextPage || (!!collectionName && !collectionEntries),
  };
};
